"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTaskKindConfig = exports.FALLBACK_KIND_CONFIG = exports.TASK_KIND_REGISTRY = void 0;
const event_names_1 = require("./event-names");
// Default thresholds for kinds without a kind-specific escalation curve.
const DEFAULT_THRESHOLDS = {
    urgent: { hours: 24 },
    week: { days: 7 },
    later: { days: 30 },
};
// Single source of truth for Assistant task kinds.
// `description` is the kind in one sentence — WHY a dossier carrying this task
// is waiting. It is what the Monday report (issue #737) prints under each
// dossier, so a kind without one says nothing more than its displayName.
// Adding a new kind = one entry here (+ a predicate + a frontend handler if needed).
// Per spec FR-033, three kinds ship wired end-to-end: `no-upload`,
// `form-validation`, `urban-analysis`. All other kinds are declared so the
// panel can render their card styles; their event bindings stay empty until a
// follow-up ticket wires each one.
exports.TASK_KIND_REGISTRY = {
    'no-upload': {
        kind: 'no-upload',
        displayName: 'Aucun document',
        description: "Aucun des documents attendus n'a été déposé sur le dossier.",
        icon: 'alert',
        accentColor: '#cf1322',
        accentBg: '#fff1f0',
        actionLabel: 'Ouvrir le dossier',
        handlerId: 'openProject',
        visibilityDelay: { days: 4 },
        targetDelay: { days: 4 },
        priorityThresholds: { urgent: {}, week: { days: 3 }, later: { days: 10 } },
        createOn: [{ event: event_names_1.EVENT_NAMES.projectCreated }],
        deleteOn: [
            {
                event: event_names_1.EVENT_NAMES.projectFileUploaded,
                predicateId: 'all-required-documents-provided',
            },
        ],
    },
    'form-validation': {
        kind: 'form-validation',
        displayName: 'Validation des formulaires',
        description: 'Des formulaires du dossier ne sont pas encore validés.',
        icon: 'check',
        accentColor: '#c2410c',
        accentBg: '#fff7ed',
        actionLabel: 'Ouvrir les formulaires',
        handlerId: 'openProject',
        visibilityDelay: {},
        targetDelay: { days: 30 },
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [{ event: event_names_1.EVENT_NAMES.projectCreated }],
        deleteOn: [
            {
                event: event_names_1.EVENT_NAMES.formStatusChanged,
                predicateId: 'all-required-forms-valid',
            },
        ],
    },
    'urban-analysis': {
        kind: 'urban-analysis',
        displayName: 'Étude urbaine',
        description: "L'étude urbaine du dossier n'est ni terminée ni écartée.",
        icon: 'eye',
        accentColor: '#0958d9',
        accentBg: '#e6f4ff',
        actionLabel: "Ouvrir l'étude urbaine",
        handlerId: 'openVerifyView',
        visibilityDelay: {},
        targetDelay: { days: 14 },
        priorityThresholds: { urgent: { hours: 24 }, week: { days: 3 }, later: { days: 14 } },
        createOn: [{ event: event_names_1.EVENT_NAMES.projectCreated }],
        deleteOn: [
            {
                event: event_names_1.EVENT_NAMES.urbanAnalysisStatusChanged,
                predicateId: 'urban-analysis-complete',
            },
        ],
    },
    'post-rdv': {
        kind: 'post-rdv',
        displayName: 'Sortie de RDV',
        description: "Les attendus de sortie de rendez-vous n'ont pas été envoyés.",
        icon: 'send',
        accentColor: '#cf1322',
        accentBg: '#fff1f0',
        actionLabel: 'Envoyer les attendus',
        handlerId: 'sendPostRdvBundle',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    relance: {
        kind: 'relance',
        displayName: 'Relance',
        description: 'Le dossier attend une relance.',
        icon: 'send',
        accentColor: '#c2410c',
        accentBg: '#fff7ed',
        actionLabel: 'Envoyer relance',
        handlerId: 'sendReminder',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    verify: {
        kind: 'verify',
        displayName: 'Vérifier',
        description: 'Le dossier attend une vérification.',
        icon: 'check',
        accentColor: '#c2410c',
        accentBg: '#fff7ed',
        actionLabel: 'Vérifier',
        handlerId: 'openVerifyView',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    deadline: {
        kind: 'deadline',
        displayName: 'Échéance',
        description: 'Une échéance du dossier arrive à son terme.',
        icon: 'clock',
        accentColor: '#cf1322',
        accentBg: '#fff1f0',
        actionLabel: 'Ouvrir le dossier',
        handlerId: 'openProject',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    blocked: {
        kind: 'blocked',
        displayName: 'Bloqué',
        description: 'Le dossier est bloqué et attend un élément extérieur.',
        icon: 'warning',
        accentColor: '#531dab',
        accentBg: '#f9f0ff',
        actionLabel: 'Relancer',
        handlerId: 'sendReminder',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    review: {
        kind: 'review',
        displayName: 'Relecture',
        description: 'Le dossier attend une relecture.',
        icon: 'eye',
        accentColor: '#0958d9',
        accentBg: '#e6f4ff',
        actionLabel: 'Ouvrir',
        handlerId: 'openProject',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    call: {
        kind: 'call',
        displayName: 'Appel',
        description: 'Un appel est à passer sur le dossier.',
        icon: 'phone',
        accentColor: '#c2410c',
        accentBg: '#fff7ed',
        actionLabel: 'Marquer fait',
        handlerId: 'markDone',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    wait: {
        kind: 'wait',
        displayName: 'En attente',
        description: 'Le dossier attend un élément extérieur.',
        icon: 'clock',
        accentColor: '#531dab',
        accentBg: '#f9f0ff',
        actionLabel: 'Éditer',
        handlerId: 'openEditManual',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    ready: {
        kind: 'ready',
        displayName: 'Prêt',
        description: 'Le dossier est prêt à être rédigé.',
        icon: 'flag',
        accentColor: '#389e0d',
        accentBg: '#f6ffed',
        actionLabel: 'Démarrer',
        handlerId: 'startDrafting',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    manual: {
        kind: 'manual',
        displayName: 'Manuel',
        description: 'Tâche ajoutée à la main sur le dossier.',
        icon: 'pushpin',
        accentColor: 'rgba(0,0,0,0.45)',
        accentBg: '#fafafa',
        actionLabel: 'Marquer fait',
        handlerId: 'markDone',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [],
        deleteOn: [],
    },
    // Folder-deadline reminders. All four are event-driven: the engine inserts
    // the task immediately on the trigger event, but visibility is deferred
    // via `occurredAt` for the deadline-bound kinds (`dossier-decision-overdue`,
    // `urban-analysis-followup`).
    'dossier-decision-overdue': {
        kind: 'dossier-decision-overdue',
        displayName: "Délai d'instruction dépassé",
        description: "Le délai d'instruction est écoulé et la mairie n'a rendu aucune décision.",
        icon: 'clock',
        accentColor: '#cf1322',
        accentBg: '#fff1f0',
        actionLabel: 'Vérifier la décision',
        handlerId: 'openProject',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [{ event: event_names_1.EVENT_NAMES.projectDepositDeadline }],
        deleteOn: [
            {
                event: event_names_1.EVENT_NAMES.projectAdministrativeDecisionRecorded,
                predicateId: 'administrative-decision-recorded',
            },
            // Note: `project.closed` also resolves this task, but via the engine's
            // blanket close (every open task for a closed project is completed),
            // not via a per-kind binding here.
        ],
    },
    // The commercial reported a modification demand from the mairie. The dossier
    // is at PROJECT_FILE_SUBMITTED and DELIBERATELY stays there (F8b) — recording
    // an answer changes no step — so nothing in the step machine would put this on
    // the architect's list. This task is that surface.
    //
    // Closed by `projectClosed` via the engine's blanket close, and by the
    // architect reopening the drafting, which is the act of handling it.
    'dossier-modification-requested': {
        kind: 'dossier-modification-requested',
        displayName: 'Modification demandée par la mairie',
        description: 'La mairie demande une modification du dossier déposé.',
        icon: 'alert',
        accentColor: '#b45309',
        accentBg: '#fff7ed',
        actionLabel: 'Reprendre le dossier',
        handlerId: 'openProject',
        visibilityDelay: {},
        targetDelay: { days: 7 },
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [{ event: event_names_1.EVENT_NAMES.projectMairieDecisionReported }],
        deleteOn: [{ event: event_names_1.EVENT_NAMES.projectSentToMairie }],
    },
    'dossier-receipt-missing': {
        kind: 'dossier-receipt-missing',
        displayName: 'Récépissé manquant',
        description: "Le récépissé de dépôt n'a pas été versé au dossier.",
        icon: 'alert',
        accentColor: '#c2410c',
        accentBg: '#fff7ed',
        actionLabel: 'Téléverser le récépissé',
        handlerId: 'openProject',
        visibilityDelay: {},
        targetDelay: { days: 7 },
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [{ event: event_names_1.EVENT_NAMES.projectDeposited }],
        deleteOn: [
            {
                event: event_names_1.EVENT_NAMES.projectFileUploaded,
                predicateId: 'deposit-receipt-uploaded',
            },
            // Note: `project.closed` also resolves this task, but via the engine's
            // blanket close (every open task for a closed project is completed),
            // not via a per-kind binding here.
        ],
    },
    'urban-analysis-followup': {
        kind: 'urban-analysis-followup',
        displayName: 'Étude urbaine sans suite',
        description: "L'étude urbaine n'a donné lieu à aucun dossier depuis trois mois.",
        icon: 'eye',
        accentColor: '#0958d9',
        accentBg: '#e6f4ff',
        actionLabel: "Ouvrir l'étude",
        handlerId: 'openUrbanAnalysis',
        visibilityDelay: {},
        targetDelay: {},
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [{ event: event_names_1.EVENT_NAMES.urbanAnalysisThreeMonthTick }],
        deleteOn: [
            {
                event: event_names_1.EVENT_NAMES.urbanAnalysisLinkedToProject,
                predicateId: 'urban-analysis-linked-to-project',
            },
        ],
    },
    'urban-analysis-send-reminder': {
        kind: 'urban-analysis-send-reminder',
        displayName: 'Étude urbaine à envoyer',
        description: "L'étude urbaine est validée mais n'a pas encore été envoyée.",
        icon: 'send',
        accentColor: '#c2410c',
        accentBg: '#fff7ed',
        actionLabel: "Ouvrir l'étude",
        handlerId: 'openUrbanAnalysis',
        visibilityDelay: {},
        targetDelay: { days: 3 },
        priorityThresholds: DEFAULT_THRESHOLDS,
        createOn: [{ event: event_names_1.EVENT_NAMES.urbanAnalysisValidated }],
        deleteOn: [],
    },
};
exports.FALLBACK_KIND_CONFIG = {
    kind: 'unknown',
    displayName: 'Inconnu',
    icon: 'info',
    accentColor: 'rgba(0,0,0,0.45)',
    accentBg: '#fafafa',
    actionLabel: 'Non disponible',
    handlerId: '__noop__',
    visibilityDelay: {},
    targetDelay: {},
    priorityThresholds: DEFAULT_THRESHOLDS,
    createOn: [],
    deleteOn: [],
};
const getTaskKindConfig = (kind) => exports.TASK_KIND_REGISTRY[kind] ?? exports.FALLBACK_KIND_CONFIG;
exports.getTaskKindConfig = getTaskKindConfig;
//# sourceMappingURL=task-kind-registry.js.map